import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
    Aangifte, AdminEntity, BalanceProvider, BokkzAlgDataService, BokkzIbDataService, ClientEntity,
    IncomeTaxEntity, KolommenbalansService, Onderneming, Ondernemingsvorm, PmClient
} from '@snelstart-ventures/fiscaal/domain-logic';
import { SelectFormControlItem, SharedUiFormControlsModule } from '@snelstart/ui-form-controls';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { BehaviorSubject, catchError, combineLatest, filter, Observable, of, Subscription, throwError } from 'rxjs';
import { ToastService } from '@snelstart/ui-toast';
import { ButtonConfiguration, SharedUiButtonModule } from '@snelstart/ui-button';
import { Event, NavigationEnd, Router } from '@angular/router';
import { SharedUiLoadingModule } from '@snelstart/ui-loading';
import { SharedUiAlertModule } from '@snelstart/ui-alert';
import { SharedUiDetailsModule } from '@snelstart/ui-details';
import { CommonModule } from '@angular/common';

@Component({
    selector: 'snelstart-venture-fiscaal-feature-administratie-koppelen-ib',
    templateUrl: './administratie-koppelen.component-ib.html',
    styleUrls: ['./administratie-koppelen.component-ib.scss'],
    imports: [
        SharedUiLoadingModule,
        SharedUiButtonModule,
        SharedUiAlertModule,
        ReactiveFormsModule,
        SharedUiDetailsModule,
        SharedUiFormControlsModule,
        CommonModule
    ]
})
export class AdministratieKoppelenIbComponent implements OnInit, OnDestroy {

    @Input() public id!: string;
    public sstRelatieId!: string;

    private routerSubscription: Subscription | undefined;
    private incomeTaxEntitySubscription: Subscription | undefined;
    private clientEntitySubscription: Subscription | undefined;

    public incomeTaxEntity = {} as IncomeTaxEntity;
    public gekoppeld = false;
    public focusedElementId: string | null = null;
    public administratieIdentifier = {} as string;

    public partnerBoolean = false;
    @Input() partner = new BehaviorSubject<boolean>(false);

    public clientEntity = {} as ClientEntity;
    public sstAdminIdFg = {} as FormGroup;
    public adminEntityList = {} as AdminEntity[];
    public administrationList: SelectFormControlItem<string>[] = [];

    public showSpinner = false;
    private isSaving = false;

    public isDefAangifte = {} as boolean;
    public heeftSamenwerkingsverband = {} as boolean;

    public hasNoAdminEntities = false;
    public hasKoppeling = false;

    public hasExternalProvider = false;
    public externalProviderId = {} as string;
    public loginStatus = false;

    public options: SelectFormControlItem<string>[] = [];
    public ondernemingFG = {} as FormGroup;

    public optionsJaNee: SelectFormControlItem<boolean>[] = [
        { value: true, label: 'Ja' },
        { value: false, label: 'Nee' }
    ];

    config = ButtonConfiguration.create({
        onClick: () =>
            new Observable((subscription) => {
                setTimeout(() => {
                    subscription.next(true);
                    subscription.complete();
                    this.getImportAdmin();
                }, 2000);
                setTimeout(() => {
                    this.setKoppeling();
                }, 2000);
            })
    });

    constructor(
        public dataService: BokkzIbDataService, public dataAlgService: BokkzAlgDataService, private router: Router,
        public toastService: ToastService,
        private pmClient: PmClient,
        private kolommenbalansService: KolommenbalansService, private fb: FormBuilder) {
        this.sstAdminIdFg = this.fb.group({
            inlezen: null as unknown as boolean,
            adminId: null as unknown as string,
            adminName: null as unknown as string
        });
        this.ondernemingFG = this.fb.group({
            ondernemingen: this.fb.array<Onderneming>([])
        });
    }

    get ondernemingen(): FormArray {
        return this.ondernemingFG.get('ondernemingen') as FormArray;
    }

    verwijderAdministratie(index: number): void {
        this.ondernemingen.at(index).get('administratieId')?.setValue('');
        this.saveForm();
        this.setKoppeling();
    }

    updateForm() {
        let lijst = this.incomeTaxEntity.aangifte?.box1?.winstUitOnderneming?.ondernemingen || [];
        if (this.partnerBoolean) {
            lijst = this.incomeTaxEntity.aangifte?.partnerAangifte?.box1?.winstUitOnderneming?.ondernemingen || [];
        }
        const formArray = this.ondernemingen as FormArray;
        const focusedControl = this.focusedElementId ? document.getElementById(this.focusedElementId) as HTMLElement : null;
        while (formArray.length > lijst.length) {
            formArray.removeAt(formArray.length - 1);
        }
        for (let i = 0; i < lijst.length; i++) {
            if (i < formArray.length) {
                const control = formArray.at(i) as FormGroup;
                control.patchValue(lijst[i]);
            } else {
                const formGroup = this.dataService.createOndernemingFormGroup(lijst[i]);
                formArray.push(formGroup);
            }
        }
        if (focusedControl && document.contains(focusedControl)) {
            focusedControl.focus();
        }
    }

    onSelect(onderneming: Onderneming) {
        const selectedAdmin = this.clientEntity.adminEntities?.find(
            option => option.administrationId === onderneming.administratieId);
        if (selectedAdmin) {
            if (selectedAdmin.providerId && selectedAdmin.administrationId) {
                this.hasExternalProvider = true;
                this.externalProviderId = selectedAdmin.providerId;
                this.checkExterneKoppeling(selectedAdmin);
            } else {
                this.hasExternalProvider = false;
                this.externalProviderId = null as unknown as string;
            }
        }
        setTimeout(() => {
            this.saveForm();
        }, 1000);
    }

    ngOnInit(): void {
        this.dataService.heeftSamenwerkingsverbandWuo.subscribe((heeftSamenwerkingsverband) => {
            this.heeftSamenwerkingsverband = heeftSamenwerkingsverband;
        });
        const incomeTaxEntityObservable = this.dataService.incomeTaxEntity.pipe(
            catchError((error) => {
                this.toastService.showErrorMessage(
                    'Error loading income tax entity data'
                );
                console.log(error);
                return of(null);
            })
        );
        const clientEntityObservable = this.dataAlgService.client.pipe(
            catchError((error) => {
                this.toastService.showErrorMessage('Error loading client entity data');
                console.log(error);
                return of(null);
            })
        );
        combineLatest([incomeTaxEntityObservable, clientEntityObservable]).subscribe(([incomeTaxEntity, clientEntity]) => {
            if (incomeTaxEntity && clientEntity) {
                this.partner.subscribe((partnerBoolean) => {
                    this.sstAdminIdFg.reset();
                    this.partnerBoolean = partnerBoolean;
                    this.incomeTaxEntity = incomeTaxEntity;
                    this.clientEntity = clientEntity;
                    this.sstRelatieId = clientEntity.sstRelationId!;
                    this.hasNoAdminEntities = this.clientEntity.adminEntities === undefined;
                    if (clientEntity) {
                        this.administrationList = [];
                        const value = clientEntity;
                        if (value.adminEntities !== undefined) {
                            this.adminEntityList = value.adminEntities;
                            for (let i = 0; this.adminEntityList[i]; i++) {
                                if (this.adminEntityList[i].administrationId) {
                                    const admininistration = {
                                        value: this.adminEntityList[i].administrationId!.toString(),
                                        label: this.adminEntityList[i].name!.toString(),
                                        providerId: this.adminEntityList[i].providerId ? this.adminEntityList[i].providerId.toString() : null
                                    };
                                    this.administrationList.push(admininistration);
                                }
                            }
                        }
                    }
                    if (incomeTaxEntity.aangifte) {
                        if (!this.partnerBoolean && this.incomeTaxEntity.aangifte?.box1?.winstUitOnderneming?.ondernemingen.some(a => a.administratieId)) {
                            this.gekoppeld = true;
                            const id = this.incomeTaxEntity.aangifte?.box1?.winstUitOnderneming?.ondernemingen[0].administratieId;
                            const gekoppeldeAdmin = this.adminEntityList.find((item) => item.administrationId?.toString() === id);
                            if (gekoppeldeAdmin !== undefined) {
                                this.hasExternalProvider = this.checkExterneKoppeling(gekoppeldeAdmin);
                                this.sstAdminIdFg.patchValue(
                                    {
                                        inlezen: true,
                                        adminId: gekoppeldeAdmin.administrationId,
                                        adminName: gekoppeldeAdmin.name,
                                        providerId: gekoppeldeAdmin.providerId
                                    },
                                    { emitEvent: false }
                                );
                            }
                        }
                        if (this.partnerBoolean) {
                            if (this.incomeTaxEntity.aangifte?.partnerAangifte?.box1?.winstUitOnderneming?.ondernemingen.some(a => a.administratieId)) {
                                this.gekoppeld = true;
                                const id = this.incomeTaxEntity.aangifte?.partnerAangifte?.box1?.winstUitOnderneming?.ondernemingen[0].administratieId;
                                const gekoppeldeAdmin = this.adminEntityList.find((item) => item.administrationId?.toString() === id);
                                if (gekoppeldeAdmin !== undefined) {
                                    this.hasExternalProvider = this.checkExterneKoppeling(gekoppeldeAdmin);
                                    this.sstAdminIdFg.patchValue(
                                        {
                                            inlezen: true,
                                            adminId: gekoppeldeAdmin.administrationId,
                                            adminName: gekoppeldeAdmin.name,
                                            providerId: gekoppeldeAdmin.providerId
                                        },
                                        { emitEvent: false }
                                    );
                                }
                            } else {
                                this.gekoppeld = false;
                            }
                        }
                        this.updateForm();
                    }
                });
            } else {
                this.toastService.showErrorMessage('Bepaalde gegevens zijn niet geladen.');
            }
        });
        this.routerSubscription = this.router.events
            .pipe(filter((event: Event): event is NavigationEnd => event instanceof NavigationEnd))
            .subscribe((event: NavigationEnd) => {
                this.handleUrlChange(event.urlAfterRedirects);
            });
    }

    ngOnDestroy(): void {
        if (this.routerSubscription) {
            this.routerSubscription.unsubscribe(); // Unsubscribe to avoid memory leaks
        }
        if (this.incomeTaxEntitySubscription) {
            this.incomeTaxEntitySubscription.unsubscribe();
        }
        if (this.clientEntitySubscription) {
            this.clientEntitySubscription.unsubscribe();
        }
    }

    handleUrlChange(newUrl: string) {
        if (!this.isSaving && this.hasUnsavedChanges()) {
            this.isSaving = true;
            this.dataService.postIncomeTaxEntity(this.sstRelatieId, false, this.incomeTaxEntity);
            this.isSaving = false;
        }
    }

    private hasUnsavedChanges(): boolean {
        return this.sstAdminIdFg.dirty;
    }

    checkData() {
        if (this.sstAdminIdFg.get('inlezen')?.value === false) {
            this.incomeTaxEntity.sstAdminId = undefined;
            this.sstAdminIdFg.patchValue({
                inlezen: false,
                adminId: null,
                adminName: null
            });
            // this.ondernemingFG.reset()
            // if (
            //     this.incomeTaxEntity.aangifte &&
            //     this.incomeTaxEntity.aangifte.balansEnResultatenrekening &&
            //     this.incomeTaxEntity.aangifte.balansEnResultatenrekening.administratieId
            // ) {
            //     this.incomeTaxEntity.aangifte.balansEnResultatenrekening.administratieId =
            //         undefined;
            // }
            // this.saveForm();
        }
    }

    getImportAdmin() {
        if (!this.partnerBoolean && this.incomeTaxEntity.aangifte?.box1?.winstUitOnderneming?.ondernemingen.filter(a => a.administratieId)) {
            this.incomeTaxEntity.aangifte?.box1?.winstUitOnderneming?.ondernemingen.forEach((onderneming) => {
                if (onderneming.administratieId) {
                    if (!this.hasExternalProvider) {
                        this.kolommenbalansService
                            .getUpdatedAangifteMetAdministratie(onderneming.administratieId, String(this.sstRelatieId), this.incomeTaxEntity, this.partnerBoolean, this.clientEntity.sstPrimaryAdminId!)
                            .then((entity) => {
                                if (entity) {
                                    this.incomeTaxEntity = entity as IncomeTaxEntity;
                                    this.dataService.updateIncomeTaxEntity(entity as IncomeTaxEntity);
                                    this.toastService.showSuccessMessage(`De administratie ${this.incomeTaxEntity.aangifte?.aangifteJaar} van '${this.sstAdminIdFg.get('adminName')?.value}' is succesvol ingelezen.`);
                                }
                            }).catch((error) => {
                            if (error.body) {
                                this.toastService.showErrorMessage(error.body);
                            }
                            if (error.error) {
                                this.toastService.showErrorMessage(error.error);
                            } else {
                                this.toastService.showErrorMessage('Helaas is er een technisch probleem waardoor de koppeling niet succesvol is verlopen. Wij werken hard aan een oplossing. In de tussentijd kun je verder met de aangifte door de gegevens zelf in te vullen. Onze excuses voor het ongemak.');
                            }
                        });
                    } else {
                        this.executeBalanceBaseProvider(this.incomeTaxEntity, this.partnerBoolean, onderneming.administratieId);
                    }
                } else {
                    this.toastService.showErrorMessage(
                        'Het is helaas niet gelukt de administratie in te lezen in de aangifte. Je kunt wel verder met de aangifte door de gegevens zelf in te vullen. Onze excuses voor het ongemak.'
                    );
                }
            });
        }
        if (this.partnerBoolean && this.incomeTaxEntity.aangifte?.partnerAangifte?.box1?.winstUitOnderneming?.ondernemingen.filter(a => a.administratieId)) {
            this.incomeTaxEntity.aangifte?.partnerAangifte?.box1?.winstUitOnderneming?.ondernemingen.forEach((onderneming) => {
                if (onderneming.administratieId) {
                    if (!this.hasExternalProvider) {
                        this.kolommenbalansService
                            .getUpdatedAangifteMetAdministratie(onderneming.administratieId, String(this.sstRelatieId), this.incomeTaxEntity, this.partnerBoolean, this.clientEntity.sstPrimaryAdminId)
                            .then((entity) => {
                                if (entity) {
                                    this.incomeTaxEntity = entity as IncomeTaxEntity;
                                    this.dataService.updateIncomeTaxEntity(entity as IncomeTaxEntity);
                                    this.toastService.showSuccessMessage(`De administratie ${this.incomeTaxEntity.aangifte?.aangifteJaar} van '${this.sstAdminIdFg.get('adminName')?.value}' is succesvol ingelezen.`);
                                }
                            })
                            .catch((error) => {
                                if (error.body) {
                                    this.toastService.showErrorMessage(error.body);
                                }
                                if (error.error) {
                                    this.toastService.showErrorMessage(error.error);
                                } else {
                                    this.toastService.showErrorMessage('Helaas is er een technisch probleem waardoor de koppeling niet succesvol is verlopen. Wij werken hard aan een oplossing. In de tussentijd kun je verder met de aangifte door de gegevens zelf in te vullen. Onze excuses voor het ongemak.');
                                }

                            });
                    } else {
                        this.executeBalanceBaseProvider(this.incomeTaxEntity, this.partnerBoolean, onderneming.administratieId);
                    }
                } else {
                    this.toastService.showErrorMessage(
                        'Het is helaas niet gelukt de administratie in te lezen in de aangifte. Je kunt wel verder met de aangifte door de gegevens zelf in te vullen. Onze excuses voor het ongemak.'
                    );
                }
            });
        }
        this.saveForm();
    }

    getSelectedAdministratieIds(): string[] {
        return this.ondernemingen.controls
            .map(onderneming => onderneming.get('administratieId')?.value)
            .filter(id => id);
    }

    getFilteredOptions(index: number): SelectFormControlItem<string>[] {
        const selectedIds = this.getSelectedAdministratieIds();
        return this.administrationList.filter(option =>
            option.value === this.ondernemingen.at(index).get('administratieId')?.value || !selectedIds.includes(option.value)
        );
    }

    saveForm(): void {
        this.ondernemingFG.updateValueAndValidity();
        const rawOndernemingen = this.ondernemingFG.value.ondernemingen;
        const sanitizedOndernemingen = this.sanitizeOndernemingen(rawOndernemingen);
        if (!this.partnerBoolean) {
            this.incomeTaxEntity.aangifte = {
                ...this.incomeTaxEntity.aangifte,
                box1: {
                    ...this.incomeTaxEntity.aangifte?.box1,
                    winstUitOnderneming: {
                        ...this.incomeTaxEntity.aangifte?.box1?.winstUitOnderneming,
                        ondernemingen: sanitizedOndernemingen
                    }
                }
            } as Aangifte;
            this.dataService.updateIncomeTaxEntity(this.incomeTaxEntity);
        }
        if (this.partnerBoolean) {
            this.incomeTaxEntity.aangifte = {
                ...this.incomeTaxEntity.aangifte,
                partnerAangifte: {
                    ...this.incomeTaxEntity.aangifte?.partnerAangifte,
                    box1: {
                        ...this.incomeTaxEntity.aangifte?.partnerAangifte?.box1,
                        winstUitOnderneming: {
                            ...this.incomeTaxEntity.aangifte?.partnerAangifte?.box1?.winstUitOnderneming,
                            ondernemingen: sanitizedOndernemingen
                        }
                    }
                }
            } as Aangifte;
            this.dataService.updateIncomeTaxEntity(this.incomeTaxEntity);
        }
    }

    isAnyAdministratieIdSelected(): boolean {
        return this.ondernemingen.controls.some(onderneming => onderneming.get('administratieId')?.value);
    }

    removeBalansActivaPassiva(incomeTaxEntity: IncomeTaxEntity): void {
        if (!this.partnerBoolean && incomeTaxEntity.aangifte) {
            if (incomeTaxEntity.aangifte.box1?.winstUitOnderneming?.ondernemingen) {
                incomeTaxEntity.aangifte.box1.winstUitOnderneming.ondernemingen.forEach(onderneming => {
                    if (onderneming.wuoOpbrengsten) {
                        onderneming.wuoOpbrengsten = undefined;
                    }
                    if (onderneming.wuoVrijgesteldeWinstbestanddelen) {
                        onderneming.wuoVrijgesteldeWinstbestanddelen = undefined;
                    }
                    if (onderneming.wuoStakingswinsten) {
                        onderneming.wuoStakingswinsten = undefined;
                    }
                    if (onderneming.financieleBaten) {
                        onderneming.financieleBaten = undefined;
                    }
                    if (onderneming.buitengewoneBaten) {
                        onderneming.buitengewoneBaten = undefined;
                    }
                    if (onderneming.wuoKosten) {
                        onderneming.wuoKosten = undefined;
                    }
                    if (onderneming.financieleLasten) {
                        onderneming.financieleLasten = undefined;
                    }
                    if (onderneming.buitengewoneLasten) {
                        onderneming.buitengewoneLasten = undefined;
                    }
                    if (onderneming.immaterieleVasteActiva) {
                        onderneming.immaterieleVasteActiva = undefined;
                    }
                    if (onderneming.materieleVasteActiva) {
                        onderneming.materieleVasteActiva = undefined;
                    }
                    if (onderneming.financieleVasteActiva) {
                        onderneming.financieleVasteActiva = undefined;
                    }
                    if (onderneming.vlottendeActivaVoorraad) {
                        onderneming.vlottendeActivaVoorraad = undefined;
                    }
                    if (onderneming.vlottendeActivaVorderingen) {
                        onderneming.vlottendeActivaVorderingen = undefined;
                    }
                    if (onderneming.effecten) {
                        onderneming.effecten = undefined;
                    }
                    if (onderneming.liquideMiddelen) {
                        onderneming.liquideMiddelen = undefined;
                    }
                    if (onderneming.eigenVermogen) {
                        onderneming.eigenVermogen = undefined;
                    }
                    if (onderneming.voorzieningen) {
                        onderneming.voorzieningen = undefined;
                    }
                    if (onderneming.langlopendeSchulden) {
                        onderneming.langlopendeSchulden = undefined;
                    }
                    if (onderneming.kortlopendeSchulden) {
                        onderneming.kortlopendeSchulden = undefined;
                    }
                    if (onderneming.investeringen) {
                        onderneming.investeringen = undefined;
                    }
                    if (onderneming.restantInvesteringsaftrek) {
                        onderneming.restantInvesteringsaftrek = undefined;
                    }
                    if (onderneming.onttrekkingenOverig) {
                        onderneming.onttrekkingenOverig = undefined;
                    }
                    if (onderneming.onttrekkingOG) {
                        onderneming.onttrekkingOG = undefined;
                    }
                    if (onderneming.onttrekkingPriveGebruikAuto) {
                        onderneming.onttrekkingPriveGebruikAuto = undefined;
                    }
                    if (onderneming.onttrekkingPriveGebruikWoning) {
                        onderneming.onttrekkingPriveGebruikWoning = undefined;
                    }
                    if (onderneming.stortingenOverig) {
                        onderneming.stortingenOverig = undefined;
                    }
                    if (onderneming.stortingOG) {
                        onderneming.stortingOG = undefined;
                    }
                    if (onderneming.nietAftrekbareKosten) {
                        onderneming.nietAftrekbareKosten = undefined;
                    }
                    if (onderneming.beperktAftrekbareKosten) {
                        onderneming.beperktAftrekbareKosten = undefined;
                    }
                });
                setTimeout(() => {
                    this.dataService.updateIncomeTaxEntity(incomeTaxEntity as IncomeTaxEntity);
                    this.ondernemingFG.markAsDirty();
                }, 500);
            }
        }
        if (this.partnerBoolean && incomeTaxEntity.aangifte?.partnerAangifte) {
            if (incomeTaxEntity.aangifte.partnerAangifte?.box1?.winstUitOnderneming?.ondernemingen) {
                incomeTaxEntity.aangifte.partnerAangifte?.box1?.winstUitOnderneming?.ondernemingen.forEach(onderneming => {
                    if (onderneming.wuoOpbrengsten) {
                        onderneming.wuoOpbrengsten = undefined;
                    }
                    if (onderneming.wuoVrijgesteldeWinstbestanddelen) {
                        onderneming.wuoVrijgesteldeWinstbestanddelen = undefined;
                    }
                    if (onderneming.wuoStakingswinsten) {
                        onderneming.wuoStakingswinsten = undefined;
                    }
                    if (onderneming.financieleBaten) {
                        onderneming.financieleBaten = undefined;
                    }
                    if (onderneming.buitengewoneBaten) {
                        onderneming.buitengewoneBaten = undefined;
                    }
                    if (onderneming.wuoKosten) {
                        onderneming.wuoKosten = undefined;
                    }
                    if (onderneming.financieleLasten) {
                        onderneming.financieleLasten = undefined;
                    }
                    if (onderneming.buitengewoneLasten) {
                        onderneming.buitengewoneLasten = undefined;
                    }
                    if (onderneming.immaterieleVasteActiva) {
                        onderneming.immaterieleVasteActiva = undefined;
                    }
                    if (onderneming.materieleVasteActiva) {
                        onderneming.materieleVasteActiva = undefined;
                    }
                    if (onderneming.financieleVasteActiva) {
                        onderneming.financieleVasteActiva = undefined;
                    }
                    if (onderneming.vlottendeActivaVoorraad) {
                        onderneming.vlottendeActivaVoorraad = undefined;
                    }
                    if (onderneming.vlottendeActivaVorderingen) {
                        onderneming.vlottendeActivaVorderingen = undefined;
                    }
                    if (onderneming.effecten) {
                        onderneming.effecten = undefined;
                    }
                    if (onderneming.liquideMiddelen) {
                        onderneming.liquideMiddelen = undefined;
                    }
                    if (onderneming.eigenVermogen) {
                        onderneming.eigenVermogen = undefined;
                    }
                    if (onderneming.voorzieningen) {
                        onderneming.voorzieningen = undefined;
                    }
                    if (onderneming.langlopendeSchulden) {
                        onderneming.langlopendeSchulden = undefined;
                    }
                    if (onderneming.kortlopendeSchulden) {
                        onderneming.kortlopendeSchulden = undefined;
                    }
                    if (onderneming.investeringen) {
                        onderneming.investeringen = undefined;
                    }
                    if (onderneming.restantInvesteringsaftrek) {
                        onderneming.restantInvesteringsaftrek = undefined;
                    }
                    if (onderneming.onttrekkingenOverig) {
                        onderneming.onttrekkingenOverig = undefined;
                    }
                    if (onderneming.onttrekkingOG) {
                        onderneming.onttrekkingOG = undefined;
                    }
                    if (onderneming.onttrekkingPriveGebruikAuto) {
                        onderneming.onttrekkingPriveGebruikAuto = undefined;
                    }
                    if (onderneming.onttrekkingPriveGebruikWoning) {
                        onderneming.onttrekkingPriveGebruikWoning = undefined;
                    }
                    if (onderneming.stortingenOverig) {
                        onderneming.stortingenOverig = undefined;
                    }
                    if (onderneming.stortingOG) {
                        onderneming.stortingOG = undefined;
                    }
                    if (onderneming.nietAftrekbareKosten) {
                        onderneming.nietAftrekbareKosten = undefined;
                    }
                    if (onderneming.beperktAftrekbareKosten) {
                        onderneming.beperktAftrekbareKosten = undefined;
                    }
                });
                setTimeout(() => {
                    this.dataService.updateIncomeTaxEntity(incomeTaxEntity as IncomeTaxEntity);
                    this.ondernemingFG.markAsDirty();
                }, 500);
            }
        }

    }

    private setKoppeling() {
        this.hasKoppeling = !this.hasKoppeling;
    }

    public heeftOndernemingen(): boolean {
        if (this.partnerBoolean) {
            return !!(this.incomeTaxEntity.aangifte &&
                this.incomeTaxEntity.aangifte?.partnerAangifte &&
                this.incomeTaxEntity.aangifte?.partnerAangifte.box1 &&
                this.incomeTaxEntity.aangifte?.partnerAangifte.box1.winstUitOnderneming &&
                this.incomeTaxEntity.aangifte?.partnerAangifte.box1.winstUitOnderneming.ondernemingen &&
                this.incomeTaxEntity.aangifte?.partnerAangifte.box1.winstUitOnderneming.ondernemingen.length > 0);
        } else {
            return !!(this.incomeTaxEntity.aangifte &&
                this.incomeTaxEntity.aangifte?.box1 &&
                this.incomeTaxEntity.aangifte?.box1.winstUitOnderneming &&
                this.incomeTaxEntity.aangifte?.box1.winstUitOnderneming.ondernemingen &&
                this.incomeTaxEntity.aangifte?.box1.winstUitOnderneming.ondernemingen.length > 0);
        }
    }

    private executeBalanceBaseProvider(incomeTaxEntity: IncomeTaxEntity, partnerBoolean: boolean, adminId: string) {
        const kolommmenBalans = this.pmClient.postBalanceProviderBalances(String(this.clientEntity.sstRelationId), String(this.externalProviderId), adminId, partnerBoolean, incomeTaxEntity);
        kolommmenBalans.subscribe({
            next: (incomeTaxEntity) => {

                if (incomeTaxEntity) {
                    this.incomeTaxEntity = incomeTaxEntity as IncomeTaxEntity;
                }
            },
            error: () => {
                this.toastService.showErrorMessage(
                    'Het is helaas niet gelukt de administratie in te lezen in de aangifte. Je kunt wel verder met de aangifte door de gegevens zelf in te vullen. Onze excuses voor het ongemak.'
                );
            },
            complete: () => {
                this.saveForm();
                this.toastService.showSuccessMessage(`De administratie ${this.incomeTaxEntity.aangifte?.aangifteJaar} van '${this.sstAdminIdFg.get('adminName')?.value}' is succesvol ingelezen.`);
            }
        });
    }

    private checkExterneKoppeling(gekoppeldeAdmin: AdminEntity) {
        if (gekoppeldeAdmin.providerId) {
            this.externalProviderId = gekoppeldeAdmin.providerId;
            this.pmClient.getIdentityProviderId(String(gekoppeldeAdmin.providerId)).subscribe({
                next: (result) => {
                    this.loginStatus = result['authenticated'];
                },
                error: (error) => {
                    this.loginStatus = false;
                    this.toastService.showWarningMessage(error);
                },
                complete: () => {
                    console.log('complete ');
                }
            });

            return true;
        }
        return false;
    }

    public goToExternalProvider() {
        const selectedProviderId = this.externalProviderId;
        this.pmClient.getBalanceProvider(selectedProviderId).pipe(catchError((error) => {
                return throwError(error);
            })
        ).subscribe({
            next: (results: BalanceProvider) => {
                this.pmClient.getIdentityProviders(results.id).subscribe({
                    next: (singleResult) => {
                        console.log(singleResult.uri);
                        window.open(singleResult.uri, '_blank');
                    }
                });
            },
            error: (error) => {
                console.log(error);
                this.toastService.showWarningMessage(error);
            },
            complete: () => {
                console.log('complete');
            }
        });
    }

    sanitizeText(input: string): string {
        return input.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, ' ').trim();
    }

    sanitizeOndernemingen(ondernemingen: any[]): any[] {
        return ondernemingen.map(onderneming => ({
            ...onderneming,
            naam: this.sanitizeText(onderneming.naam)
            // Add more fields to sanitize if needed
        }));
    }

}
